import Modal from "./Modal";
import React from "react";
import {Button, ButtonStyles} from "../form/Button/Button";
import {Trans, useTranslation} from "react-i18next";
import {isCart, useUnsafeCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {excludedManufacturers} from "./ApplicableVouchersModal";
import {ICartRebateVoucher} from "@connox/typescript-library/types/cart/CartRebateVoucher";
import {
  VOUCHER_MIN_ITEM_QUANTITY_NOT_REACHED,
  VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED,
} from "../../cart/Rebates/VoucherBlock";
import {updateUrlParams} from "../../cart/CartIcon/CartIconPortal";

const VoucherAddedModal: React.FC = () => {
  const {t} = useTranslation("cart");
  const context = useUnsafeCartContext();

  if (!context || context.voucherAdded === "" || !isCart(context)) {
    return null;
  }

  const possiblePosition = context.cart.positions.find(
    (pos) => pos.type === "Voucher" && pos.reference?.voucher?.code === context.voucherAdded,
  );

  if (!possiblePosition) {
    return null;
  }

  const voucher = possiblePosition.reference?.voucher as ICartRebateVoucher;
  const isMinOrderWasNotReachedCodeSet = voucher.messages.some(
    (msg) => msg.code === VOUCHER_MIN_ORDER_VALUE_WAS_NOT_REACHED,
  );
  const isMinItemCountNotReachedCodeSet = voucher.messages.some(
    (msg) => msg.code === VOUCHER_MIN_ITEM_QUANTITY_NOT_REACHED,
  );

  const closeModal = () => context.setVoucherAdded("");

  const deleteVoucher = () => {
    void context.cart.removeVoucher(context.voucherAdded);
    closeModal();
  };

  // if voucher invalid and error code is of any other type than the tested ones
  if (!voucher.isValid && !isMinOrderWasNotReachedCodeSet && !isMinItemCountNotReachedCodeSet) {
    return (
      <Modal isVisible={true} closeFunction={closeModal}>
        {voucher.messages.map((msg) => (
          <h2 key={msg.code}>{`${t(msg.code)} (Code: ${voucher.code})`}</h2>
        ))}
        <Button styles={[ButtonStyles.big]} action={deleteVoucher}>
          {t("Gutschein entfernen")}
        </Button>
      </Modal>
    );
  }

  const continuePurchase = () => {
    closeModal();
    if (context.isIntoCheckout) {
      context.setIsIntoCheckout(false);
    }
    if (context.isCartVisible) {
      context.setIsCartVisible(false);
      updateUrlParams(null, "replace");
    }
  };

  const VoucherInfos = () => (
    <p style={{marginBottom: "2rem"}}>
      <Trans i18nKey="Alle Infos zu Gutscheinen" ns="cart">
        <a href={t("/kundenservice/gutscheine-aktionen.html#gutscheine")}>Hier</a> finden Sie alle Informationen zum
        Einlösen von Gutscheinen.
        <br />
        Wir wünschen viel Spaß beim Einkaufen!
      </Trans>
    </p>
  );

  const MinOrderValue = () => {
    if (voucher.minimumOrderValueAbsolute && voucher.minimumOrderValueAbsolute.amount !== "0.00") {
      return <p>{t("Mindesteinkaufswert: {{formatted}}", {formatted: voucher.minimumOrderValueAbsolute.formatted})}</p>;
    }

    return <p>{t("Kein Mindesteinkaufswert")}</p>;
  };

  const MinItemCount = () => {
    if (voucher.minimumOrderQuantityAbsolute > 1) {
      return <p>{t("Mindestartikelanzahl: {{quantity}}", {quantity: voucher.minimumOrderQuantityAbsolute})}</p>;
    }

    return null;
  };

  const VoucherValueInfo = () => {
    if (!voucher.voucherAmount && !voucher.voucherPercentage) {
      return null;
    }

    return (
      <p>
        {t("Wert Ihres Gutscheins: {{formatted}}", {
          formatted: voucher.voucherAmount ? voucher.voucherAmount.formatted : voucher.voucherPercentage?.formatted,
        })}
      </p>
    );
  };

  const ValidToInfo = () => {
    const {validToDate} = voucher;
    if (validToDate === "") {
      return null;
    }

    return <p>{t("Gültig bis: {{validToDate}}", {validToDate})}</p>;
  };

  // CAUTION: when any minimum value is not reached 'possiblePosition.item' is empty
  if (voucher.type === "Item" && possiblePosition.item) {
    const {item} = possiblePosition;
    const imgUrl = item.images[0].sizes.find((img) => img.name === "thumbnail-3x")?.url;

    return (
      <Modal isVisible={true} closeFunction={closeModal}>
        <h2>{t("Gute Wahl!")}</h2>
        <table style={{margin: "0 0 1rem"}}>
          <tbody>
            <tr key={item.itemId}>
              <td style={{width: "98px"}}>
                <img src={imgUrl} alt={item.title} width="90px" />
              </td>
              <td>{t("{{title}} wurde als Gratisartikel in Ihren Warenkorb gelegt.", {title: item.title})}</td>
            </tr>
          </tbody>
        </table>
        {voucher.minimumOrderValueAbsolute && voucher.minimumOrderValueAbsolute.amount !== "0.00" && (
          <p>
            {t("Bitte beachten Sie den Mindesteinkaufswert von: {{minOrderValue}}", {
              minOrderValue: voucher.minimumOrderValueAbsolute.formatted,
            })}
          </p>
        )}
        <p>{t("Bitte beachten Sie: Nur solange der Vorrat reicht!")}</p>
        <ValidToInfo />
        <VoucherInfos />
        <Button styles={[ButtonStyles.big]} action={continuePurchase}>
          {t("Weiter einkaufen")}
        </Button>
      </Modal>
    );
  }

  return (
    <Modal isVisible={true} closeFunction={closeModal}>
      <h2>{t("Ihr Gutschein wurde in den Warenkorb gelegt!")}</h2>
      <VoucherValueInfo />
      <MinOrderValue />
      <MinItemCount />
      <ValidToInfo />
      <VoucherInfos />
      <Button styles={[ButtonStyles.big]} action={continuePurchase}>
        {t("Weiter einkaufen")}
      </Button>
      <br />
      <p style={{marginTop: "1.5rem"}}>
        <small>{t("Bitte beachten Sie: ... {{manufacturers}}", {manufacturers: excludedManufacturers})}</small>
      </p>
    </Modal>
  );
};

export default VoucherAddedModal;
